import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Heart, 
  Instagram, 
  Twitter, 
  Facebook,
  Youtube,
  Linkedin,
  MessageCircle,
  Mail,
  ArrowUp,
  ExternalLink,
  Play,
  Clock
} from 'lucide-react';

const SocialMediaFooter = () => {
  const navigate = useNavigate();

  const socialLinks = [
    {
      name: 'Instagram',
      icon: Instagram,
      url: 'https://instagram.com/shotclockdating',
      color: 'text-pink-500 hover:text-pink-600',
      bgColor: 'hover:bg-pink-50'
    },
    {
      name: 'TikTok',
      icon: Play,
      url: 'https://tiktok.com/@shotclockdating',
      color: 'text-white hover:text-gray-200',
      bgColor: 'hover:bg-gray-700'
    },
    {
      name: 'Twitter',
      icon: Twitter,
      url: 'https://twitter.com/shotclockdating',
      color: 'text-blue-500 hover:text-blue-600',
      bgColor: 'hover:bg-blue-50'
    },
    {
      name: 'Facebook',
      icon: Facebook,
      url: 'https://facebook.com/shotclockdating',
      color: 'text-blue-600 hover:text-blue-700',
      bgColor: 'hover:bg-blue-50'
    },
    {
      name: 'YouTube',
      icon: Youtube,
      url: 'https://www.youtube.com/@shotclockllc9268',
      color: 'text-red-500 hover:text-red-600',
      bgColor: 'hover:bg-red-50'
    }
  ];

  const quickLinks = [
    { name: 'How It Works', path: '/how-it-works' },
    { name: 'Advanced Features', path: '/advanced-features' },
    { name: 'Pricing', path: '/subscription/plans' },
    { name: 'Support', path: '/support' }
  ];

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 text-white">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          
          {/* Brand Section */}
          <div className="lg:col-span-1">
            <motion.div 
              className="flex items-center mb-4"
              whileHover={{ scale: 1.02 }}
            >
              <div className="h-12 w-12 mr-3 flex items-center justify-center bg-white rounded-xl p-1">
                <img 
                  src="/shot-clock-icon.png" 
                  alt="Shot Clock Dating Icon"
                  className="h-10 w-10 object-contain"
                />
              </div>
              <div>
                <h3 className="text-xl font-bold">Shot Clock Dating</h3>
                <p className="text-sm text-gray-400">AI-Powered Speed Dating</p>
              </div>
            </motion.div>
            
            <p className="text-gray-300 text-sm mb-4 leading-relaxed">
              The world's first AI-powered speed dating platform. Connect with compatible 
              singles through timed video conversations.
            </p>
            
            <div className="flex items-center space-x-4 text-sm text-gray-400">
              <div className="flex items-center">
                <div className="w-4 h-4 mr-1 flex items-center justify-center">
                  <img 
                    src="/shot-clock-icon-privacy.png" 
                    alt="Shot Clock Dating Icon"
                    className="w-4 h-4 object-contain"
                  />
                </div>
                12,847 Matches
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div className="lg:col-span-1">
            <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <motion.li 
                  key={index}
                  whileHover={{ x: 5 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <button
                    onClick={() => navigate(link.path)}
                    className="text-gray-300 hover:text-rose-400 transition-colors text-sm flex items-center"
                  >
                    {link.name}
                    <ExternalLink className="w-3 h-3 ml-1 opacity-50" />
                  </button>
                </motion.li>
              ))}
            </ul>
            
            <div className="mt-6">
              <h5 className="text-sm font-medium mb-2 text-gray-200">Contact Us</h5>
              <div className="space-y-2 text-sm text-gray-400">
                <div className="flex items-center">
                  <Mail className="w-4 h-4 mr-2" />
                  <a 
                    href="mailto:admin@shotclockdating.com"
                    className="hover:text-white transition-colors cursor-pointer"
                  >
                    admin@shotclockdating.com
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Social Media Section */}
          <div className="lg:col-span-2">
            <h4 className="text-lg font-semibold mb-4">Follow Our Journey</h4>
            <p className="text-gray-300 text-sm mb-6">
              Join our community on social media for dating tips, success stories, and platform updates!
            </p>
            
            {/* Social Media Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
              {socialLinks.map((social, index) => (
                <motion.a
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`group flex flex-col items-center p-4 rounded-lg border border-gray-700 bg-gray-800/50 ${social.bgColor} ${social.color} transition-all duration-300 hover:border-gray-600`}
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <social.icon className={`w-6 h-6 mb-2 transition-colors ${social.color}`} />
                  <span className="text-xs font-medium">{social.name}</span>
                </motion.a>
              ))}
            </div>

            {/* Social Media CTA */}
            <div className="bg-gradient-to-r from-rose-500/20 to-pink-500/20 rounded-lg p-4 border border-rose-500/30">
              <div className="flex items-center justify-between">
                <div>
                  <h5 className="font-medium text-rose-300 mb-1">Stay Connected!</h5>
                  <p className="text-xs text-gray-300">Get dating tips & success stories</p>
                </div>
                <motion.button
                  onClick={() => window.open('https://instagram.com/shotclockdating', '_blank')}
                  className="bg-rose-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-rose-600 transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Follow Us
                </motion.button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-700 bg-gray-900/50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row items-center justify-between">
            <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-6 text-sm text-gray-400">
              <p>&copy; 2025 Shot Clock Dating. All rights reserved.</p>
              <div className="flex items-center space-x-4">
                <button 
                  onClick={() => navigate('/privacy-policy')}
                  className="hover:text-rose-400 transition-colors cursor-pointer"
                >
                  Privacy Policy
                </button>
                <button 
                  onClick={() => navigate('/terms-of-service')}
                  className="hover:text-rose-400 transition-colors cursor-pointer"
                >
                  Terms of Service
                </button>
                <button 
                  onClick={() => navigate('/community-guidelines')}
                  className="hover:text-rose-400 transition-colors cursor-pointer"
                  data-testid="footer-community-guidelines-link"
                >
                  Community Guidelines
                </button>
                <button 
                  onClick={() => navigate('/cookie-policy')}
                  className="hover:text-rose-400 transition-colors cursor-pointer"
                >
                  Cookie Policy
                </button>
                <button 
                  onClick={() => navigate('/delete-account')}
                  className="hover:text-rose-400 transition-colors cursor-pointer"
                  data-testid="footer-delete-account-link"
                >
                  Delete Account
                </button>
              </div>
            </div>
            
            {/* Scroll to Top Button */}
            <motion.button
              onClick={scrollToTop}
              className="mt-4 sm:mt-0 bg-rose-500 text-white p-2 rounded-full hover:bg-rose-600 transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              title="Back to Top"
            >
              <ArrowUp className="w-4 h-4" />
            </motion.button>
          </div>
        </div>
      </div>

      {/* Mobile-Optimized Social Bar */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 bg-gray-900/95 backdrop-blur-md border-t border-gray-700 z-40">
        <div className="flex items-center justify-around py-2">
          {socialLinks.slice(0, 4).map((social, index) => (
            <motion.a
              key={index}
              href={social.url}
              target="_blank"
              rel="noopener noreferrer"
              className={`p-3 rounded-full ${social.color} transition-colors`}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <social.icon className="w-5 h-5" />
            </motion.a>
          ))}
        </div>
      </div>
    </footer>
  );
};

export default SocialMediaFooter;